/**
 * Stable `data-testid`s for the object-level toolbar — the toolbar's e2e
 * selector contract (CUS-137).
 *
 * Everything else the toolbar renders is text- or i18n-coupled: the tray
 * buttons' accessible names are `aria-label`s carrying English literals
 * ('Save' / 'Cancel' / 'More actions'), and the overflow section headings and
 * item labels come from i18n (`objectToolbar.edit` / `.download` /
 * `.dangerZone`, plus per-org taxonomy overrides on entity nouns). A Playwright
 * page object built on those breaks on any copy change.
 *
 * The ids below are keyed on the **config** ids instead — `ToolbarAction.id` and
 * `ToolbarActionGroup.id`, which consumers declare (`'create'`, `'edit-form'`,
 * `'delete'`, `'csv'`, `'edit'`, `'download'`, `'danger'`, …) and which are
 * already stable across the CUS-136 fanout. `packages/e2e/models/components/ObjectToolbar.ts`
 * is the only consumer; keep the two in step.
 *
 * The overflow menu renders through a portal (atomic-ui `DropdownMenuContent`),
 * so its nodes are NOT inside {@link OBJECT_TOOLBAR_TEST_ID} in the DOM — locate
 * overflow items from the page root, not scoped to the toolbar container.
 */

/** The toolbar bar itself (header + action tray). */
export const OBJECT_TOOLBAR_TEST_ID = 'object-toolbar';

/** The object title (crossfaded — asserts on the *currently painted* title). */
export const OBJECT_TOOLBAR_TITLE_TEST_ID = 'object-toolbar-title';

/** The secondary counter beside the title, rendered verbatim. */
export const OBJECT_TOOLBAR_COUNTER_TEST_ID = 'object-toolbar-counter';

/** The overflow (⋮) trigger button. */
export const OBJECT_TOOLBAR_OVERFLOW_TRIGGER_TEST_ID =
  'object-toolbar-overflow-trigger';

/** The opened overflow menu surface (portalled). */
export const OBJECT_TOOLBAR_OVERFLOW_TEST_ID = 'object-toolbar-overflow';

/**
 * A tray button — primary actions plus the built-in Save / Approve / Cancel
 * controls, whose synthetic ids are {@link OBJECT_TOOLBAR_SAVE_ID},
 * {@link OBJECT_TOOLBAR_APPROVE_ID} and {@link OBJECT_TOOLBAR_CANCEL_ID}.
 */
export const objectToolbarActionTestId = (id: string): string =>
  `object-toolbar-action-${id}`;

/** An overflow group (`ToolbarActionGroup.id`), including the synthesised ones. */
export const objectToolbarGroupTestId = (id: string): string =>
  `object-toolbar-group-${id}`;

/** An overflow menu item (`ToolbarAction.id`). */
export const objectToolbarItemTestId = (id: string): string =>
  `object-toolbar-item-${id}`;

/* -------------------------------------------------------------------------- */
/*  Synthetic ids for the built-in tray controls                              */
/* -------------------------------------------------------------------------- */

/**
 * The built-in Save / Approve / Cancel controls are not declared as
 * `ToolbarAction`s (they come off `save` / `cancel` on the props), so they have
 * no consumer-supplied id. These are their fixed ids in the
 * {@link objectToolbarActionTestId} namespace.
 */
export const OBJECT_TOOLBAR_SAVE_ID = 'save';
export const OBJECT_TOOLBAR_APPROVE_ID = 'approve';
export const OBJECT_TOOLBAR_CANCEL_ID = 'cancel';
